import ScrollToTop from "@/components/feature/ScrollToTop";
import BackToTop from "@/components/feature/BackToTop";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import NewsletterForm from "@/components/feature/NewsletterForm";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";
import { brand } from "@/mocks/site";
import ContactForm from "@/pages/contact/components/ContactForm";

const channels = [
  {
    icon: "ri-mail-send-line",
    label: "线索投稿",
    value: brand.tipEmail,
    note: "适合提交带有原始材料的线索，我们会在三个工作日内反馈是否进入鉴定流程。",
  },
  {
    icon: "ri-error-warning-line",
    label: "纠错与反馈",
    value: brand.email,
    note: "认为某条结论有误？附上反证材料发到这里，核实后会公开更新。",
  },
  {
    icon: "ri-briefcase-line",
    label: "内容合作",
    value: brand.cooperateEmail,
    note: "媒体转载、内容共建、数据合作等事项请联系此邮箱。",
  },
];

const flow = [
  { step: "01", title: "提交", desc: "填写表单或直接发邮件，尽量附上原始材料与时间信息。" },
  { step: "02", title: "初筛", desc: "我们检查基本要素是否齐全，判断能否进入鉴定流程。" },
  { step: "03", title: "核实", desc: "对可核验的部分做来源分级与交叉印证，必要时联系补充材料。" },
  { step: "04", title: "反馈", desc: "通过你留下的邮箱同步进度；确认后条目会进入爆料库。" },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ContactPage",
      name: "联系我们 · 瓜田爆料",
      url: `${SITE_ORIGIN}/contact`,
      description:
        "通过瓜田爆料联系我们：提交爆料线索、反馈鉴定结论错误、洽谈内容合作，或订阅每周鉴定播报。",
      inLanguage: "zh-CN",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
        { "@type": "ListItem", position: 2, name: "联系我们", item: `${SITE_ORIGIN}/contact` },
      ],
    },
  ],
};

export default function Contact() {
  useSeo({
    title: "联系我们 | 线索投稿与纠错反馈 · 瓜田爆料",
    description:
      "瓜田爆料联系方式：投稿爆料线索、反馈鉴定结论错误、洽谈内容合作，或订阅每周鉴定播报，我们会公开处理流程与反馈周期。",
    keywords: ["联系我们", "爆料投稿", "纠错反馈", "真假鉴定", "可信度评估"],
    path: "/contact",
    ogType: "website",
    jsonLd,
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <ScrollToTop />
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100 paper-texture">
          <div className="max-w-shell mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
            <h1 className="mt-5 font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
              联系我们
            </h1>
            <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600 leading-relaxed">
              无论是手里有可核实的线索、发现我们的结论需要更正，还是想聊聊内容合作，
              都可以通过下面的渠道找到我们。所有投稿都会走同一套鉴定流程。
            </p>
          </div>
        </section>

        <section className="max-w-shell mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <div className="lg:col-span-7">
              <div className="border border-background-200 rounded-card bg-background-50 p-5 md:p-7">
                <h2 className="font-heading text-xl font-bold text-foreground-950">
                  提交线索或反馈
                </h2>
                <p className="mt-2 mb-6 text-sm text-foreground-600 leading-relaxed">
                  带星号的字段为必填。描述越具体，越容易进入鉴定流程。
                </p>
                <ContactForm />
              </div>
            </div>

            <div className="lg:col-span-5 space-y-6">
              <div className="border border-background-200 rounded-card bg-background-50 overflow-hidden">
                <div className="px-5 py-4 border-b border-background-200 bg-background-100">
                  <h2 className="font-heading text-base font-bold text-foreground-950">联系方式</h2>
                </div>
                <ul className="divide-y divide-background-200">
                  {channels.map((c) => (
                    <li key={c.label} className="p-5 flex gap-4">
                      <span className="shrink-0 w-10 h-10 flex items-center justify-center rounded-control bg-primary-50 text-primary-600">
                        <i className={`${c.icon} text-xl`}></i>
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-900">{c.label}</p>
                        <p className="mt-0.5 text-sm text-primary-700 break-all">{c.value}</p>
                        <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed">{c.note}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border border-secondary-200 rounded-card bg-secondary-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">
                  每周鉴定播报
                </h2>
                <p className="mt-2 mb-4 text-sm text-foreground-600 leading-relaxed">
                  不想错过反转？留下邮箱，一周一封，只发新增结论。
                </p>
                <NewsletterForm variant="block" />
              </div>

              <div className="border border-background-200 rounded-card bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">受理说明</h2>
                <ul className="mt-3 space-y-2.5 text-sm text-foreground-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-time-line text-foreground-400 mt-0.5"></i>
                    一般三到五个工作日内完成第一轮评估。
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-shield-line text-foreground-400 mt-0.5"></i>
                    涉及个人隐私、未成年与医疗信息的内容不予受理。
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-refresh-line text-foreground-400 mt-0.5"></i>
                    结论有更新时，条目会公开标注修改时间。
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-background-200 bg-background-100">
          <div className="max-w-shell mx-auto px-4 md:px-6 py-12 md:py-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight text-center">
              一条线索要经过什么
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 text-center max-w-2xl mx-auto leading-relaxed">
              流程固定、标准公开。不管是谁投的稿，都要走完这四步才会出结论。
            </p>
            <ol className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {flow.map((f) => (
                <li key={f.step} className="border border-background-200 rounded-card bg-background-50 p-5">
                  <span className="font-mono-num text-2xl font-bold text-primary-500">{f.step}</span>
                  <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">{f.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}